import { type ReactNode, useState } from "react";
import ModalDialog, {
  ModalDialogBody,
  ModalDialogFooter,
} from "../UI/ModalDialog";
import Button from "../UI/Button";
import Callout from "../UI/Callout";
// @ts-expect-error pure js
import { ReactSelectStandalone } from "../UI/SelectStandalone";
// @ts-expect-error pure js
import DocumentPreview from "../UI/DocumentPreview";
// @ts-expect-error pure js
import useAuthenticatedAxios from "../../hooks/use-authenticated-axios";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
import type { Tenant } from "../../models/tenant";
import type { DocumentFile } from "../../models/document";
import styles from "./TenantDecompteDialog.module.css";

const ALL_YEARS_OPTION = {
  value: null as number | null,
  label: "Toutes les années",
};

interface DecompteResult {
  file: DocumentFile;
  previewUrl: string;
}

interface TenantDecompteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenant: Tenant | null;
  years: number[];
}

export default function TenantDecompteDialog({
  open,
  onOpenChange,
  tenant,
  years,
}: TenantDecompteDialogProps) {
  const axios = useAuthenticatedAxios();
  const [year, setYear] = useState<number | null>(null);
  const [result, setResult] = useState<DecompteResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ReactNode>("");

  const yearOptions = [
    ALL_YEARS_OPTION,
    ...years.map((availableYear) => ({
      value: availableYear as number | null,
      label: String(availableYear),
    })),
  ];

  function reset() {
    setYear(null);
    clearResult();
    setError("");
  }

  function downloadResult() {
    if (!result) return;
    const link = document.createElement("a");
    link.href = result.previewUrl;
    link.download = result.file.filename ?? "decompte.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  function clearResult() {
    if (result) {
      URL.revokeObjectURL(result.previewUrl);
    }
    setResult(null);
  }

  async function generate() {
    setError("");
    setIsLoading(true);
    try {
      const response = await axios.post(`/tenants/${tenant?.id}/get-decompte`, {
        year,
      });
      const file: DocumentFile = response.data.file;
      // Blob same-origin plutôt qu'URL signée : Chrome applique
      // X-Frame-Options aux <object> et bloquerait l'embed cross-origin
      const blobResponse = await axios.get(`/documents/${file.id}/download`, {
        responseType: "blob",
      });
      setResult({ file, previewUrl: URL.createObjectURL(blobResponse.data) });
    } catch (e) {
      setError(handleAPIError(e));
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <ModalDialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) reset();
        onOpenChange(nextOpen);
      }}
      size={result ? "xl" : "sm"}
      header="Décompte locataire"
      subtitle={
        result ? undefined : "Choisissez la période puis générez le document."
      }
    >
      {result ? (
        <>
          <ModalDialogBody className={styles.body}>
            <div className={styles.preview}>
              <DocumentPreview
                mimetype={result.file.mimetype}
                name={result.file.filename}
                url={result.previewUrl}
              />
            </div>
          </ModalDialogBody>
          <ModalDialogFooter
            start={
              <Button variant="secondary" onClick={clearResult}>
                Générer un autre décompte
              </Button>
            }
          >
            <Button variant="secondary" onClick={() => onOpenChange(false)}>
              Fermer
            </Button>
            <Button onClick={downloadResult}>Télécharger</Button>
          </ModalDialogFooter>
        </>
      ) : (
        <>
          <ModalDialogBody className={styles.body}>
            <ReactSelectStandalone
              label="Année du décompte"
              options={yearOptions}
              value={
                yearOptions.find((option) => option.value === year) ??
                ALL_YEARS_OPTION
              }
              onChange={(option: { value: number | null } | null) =>
                setYear(option?.value ?? null)
              }
            />
            <Callout variant="danger">{error}</Callout>
          </ModalDialogBody>
          <ModalDialogFooter
            start={
              <Button variant="secondary" onClick={() => onOpenChange(false)}>
                Annuler
              </Button>
            }
          >
            <Button
              onClick={() => {
                generate().catch(console.error);
              }}
              disabled={isLoading}
            >
              {isLoading ? "Génération en cours…" : "Générer le décompte"}
            </Button>
          </ModalDialogFooter>
        </>
      )}
    </ModalDialog>
  );
}
